import { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const Questions = ({ event }) => {
  const [config, setConfig] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [requirePhone, setRequirePhone] = useState(false);

  useEffect(() => {
    if (event) {
      getConfig();
      getQuestions();
    }
  }, [event?._id]);

  const getConfig = async () => {
    const result = await request(`events/config/?eventId=${event._id}`);
    if (result?.success && result?.data) {
      setConfig(result.data);

      if (result.data.profileQuestions) {
        // Filter out any legacy "phone" entry; phone is now handled separately
        const filteredQuestions = result.data.profileQuestions.filter(
          (id) => id !== "phone"
        );
        setSelectedQuestions(filteredQuestions);
      }

      // Initialize phone requirement from config, with a legacy fallback if needed
      if (typeof result.data.requirePhone === "boolean") {
        setRequirePhone(result.data.requirePhone);
      } else if (
        Array.isArray(result.data.profileQuestions) &&
        result.data.profileQuestions.includes("phone")
      ) {
        setRequirePhone(true);
      }
    }
  };

  const getQuestions = async () => {
    const result = await request(`profilequestions/?eventId=${event._id}`);
    if (result.success) {
      setQuestions(result.questions);
    }
  };

  const addQuestionToCheckout = async (questionId) => {
    try {
      const updates = {
        profileQuestions: [...selectedQuestions, questionId],
      };
      const result = await request("events/config", "post", {
        eventId: event._id,
        updates,
      });
      if (result.success) {
        toast.success("Question added to checkout.");
      }
    } catch (error) {
      console.error("Error adding question:", error);
      toast.error("An error occurred while adding the question.");
    }
  };

  const removeQuestionFromCheckout = async (questionId) => {
    try {
      const updatedQuestions = selectedQuestions.filter(
        (id) => id !== questionId
      );
      const updates = {
        profileQuestions: updatedQuestions,
      };
      const result = await request("events/config", "post", {
        eventId: event._id,
        updates,
      });
      if (result.success) {
        toast.success("Question removed from checkout.");
      }
    } catch (error) {
      console.error("Error removing question:", error);
      toast.error("An error occurred while removing the question.");
    }
  };

  const updateRequirePhone = async (value) => {
    try {
      const updates = {
        requirePhone: value,
      };
      const result = await request("events/config", "post", {
        eventId: event._id,
        updates,
      });
      if (result.success) {
        toast.success(
          value
            ? "Phone requirement added to checkout."
            : "Phone requirement removed from checkout."
        );
      }
    } catch (error) {
      console.error("Error updating phone requirement:", error);
      toast.error("An error occurred while updating the phone requirement.");
    }
  };

  const handleToggleQuestion = (questionId) => {
    const isSelected = selectedQuestions.includes(questionId);
    if (isSelected) {
      setSelectedQuestions(selectedQuestions.filter((id) => id !== questionId));
      removeQuestionFromCheckout(questionId);
    } else {
      setSelectedQuestions([...selectedQuestions, questionId]);
      addQuestionToCheckout(questionId);
    }
  };

  const handleTogglePhone = () => {
    const newValue = !requirePhone;
    setRequirePhone(newValue);
    updateRequirePhone(newValue);
  };

  return (
    <Box>
      <Stack spacing={3} sx={{ pr: 3 }}>
        <Typography
          variant="body1"
          color="text.secondary"
          style={{ marginTop: 20 }}
        >
          Select which questions customers must answer when checking out.
        </Typography>
        <Typography variant="h6">Selected Questions</Typography>
        {selectedQuestions?.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No questions selected.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {selectedQuestions.map((id) => {
              const question = questions.find((q) => q._id === id);
              if (!question) return null;
              return (
                <Card
                  key={id}
                  variant="outlined"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1">
                      {question.question}
                    </Typography>
                    {question.description && (
                      <Typography variant="body2" color="text.secondary">
                        {question.description}
                      </Typography>
                    )}
                  </CardContent>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleToggleQuestion(id)}
                  >
                    Remove
                  </Button>
                </Card>
              );
            })}
          </Stack>
        )}

        <Typography variant="h6" sx={{ mt: 4 }}>
          Available Questions
        </Typography>
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="subtitle1">Phone</Typography>
                <Typography variant="body2" color="text.secondary">
                  Customers will be required to include their phone number at
                  checkout
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={requirePhone}
                    onChange={handleTogglePhone}
                    color="primary"
                  />
                }
                label={requirePhone ? "Remove" : "Add"}
              />
            </CardContent>
          </Card>
          {questions.map((q) => {
            const isSelected = selectedQuestions.includes(q._id);
            if (["multipleChoice", "range", "text"].includes(q.questionType))
              return null;

            return (
              <Card key={q._id} variant="outlined">
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">{q.question}</Typography>
                    {q.description && (
                      <Typography variant="body2" color="text.secondary">
                        {q.description}
                      </Typography>
                    )}
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleQuestion(q._id)}
                        color="primary"
                      />
                    }
                    label={isSelected ? "Remove" : "Add"}
                  />
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
};

export default Questions;
